// frontend-react/src/App.tsx
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { App as AntdApp, ConfigProvider } from 'antd';
import zhTW from 'antd/locale/zh_TW';

// ✅ 引入 reCAPTCHA V3 Provider
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import GoogleCallback from './pages/GoogleCallback';
import AdReviewPage from './pages/AdReviewPage';
import PermissionManagementPage from './pages/PermissionManagementPage';
import UserManagementPage from './pages/UserManagementPage';
import UserPermissionPage from './pages/UserPermissionPage';
import SetupPasswordPage from './pages/SetupPasswordPage';
import DepartmentManagementPage from './pages/DepartmentManagementPage';
import CompanyManagementPage from './pages/CompanyManagementPage';
import FAQPage from './pages/FAQPage';
import ContractComparisonPage from './pages/ContractComparisonPage';

import ChangePasswordPage from './pages/ChangePasswordPage';

import ProtectedRoute from './components/ProtectedRoute';
import PermissionRoute from './components/PermissionRoute';
import CompanyRoute from './components/CompanyRoute';

import Footer from './components/Footer';
import { useAuth } from './contexts/AuthContext';
import './App.css';

const App: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // 從環境變數讀取 Site Key，確保金鑰安全性
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        }}
      >
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.9)',
            padding: '2rem',
            borderRadius: '1rem',
            textAlign: 'center',
          }}
        >
          載入中...
        </div>
      </div>
    );
  }

  return (
    // ✅ 使用 GoogleReCaptchaProvider 包裹整個應用程式
    // 這裡使用你在 GCP 申請的 Enterprise Key ID [cite: 68]
    <GoogleReCaptchaProvider
      reCaptchaKey={recaptchaSiteKey}
      language='zh-TW'
      useRecaptchaNet={false} // 如果在台灣使用，建議設為 false 或預設即可
    >
      <ConfigProvider locale={zhTW}>
        <AntdApp>
          <Routes>
            {/* 公開路由 */}
            <Route path='/login' element={<LoginPage />} />
            <Route path='/auth/google/callback' element={<GoogleCallback />} />
            <Route path='/setup-password' element={<SetupPasswordPage />} />

            {/* 密碼過期導流頁（需要登入 token） */}
            <Route
              path='/change-password'
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />

            {/* 受保護的路由 */}
            <Route
              path='/'
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              }
            />

            <Route
              path='/ad-review'
              element={
                <ProtectedRoute>
                  <PermissionRoute permission='can_view_rules'>
                    <AdReviewPage />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path='/contract-comparison'
              element={
                <ProtectedRoute>
                  <ContractComparisonPage />
                </ProtectedRoute>
              }
            />

            <Route
              path='/permissions'
              element={
                <ProtectedRoute>
                  <PermissionRoute permission='can_manage_users'>
                    <PermissionManagementPage />
                  </PermissionRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path='/user-management'
              element={
                <ProtectedRoute>
                  <CompanyRoute allowedCompanies={[]}>
                    <UserManagementPage />
                  </CompanyRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path='/department-management'
              element={
                <ProtectedRoute>
                  <CompanyRoute allowedCompanies={[]}>
                    <DepartmentManagementPage />
                  </CompanyRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path='/user-permission/:userId'
              element={
                <ProtectedRoute>
                  <CompanyRoute allowedCompanies={[]}>
                    <UserPermissionPage />
                  </CompanyRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path='/company-management'
              element={
                <ProtectedRoute>
                  <CompanyManagementPage />
                </ProtectedRoute>
              }
            />

            <Route
              path='/faq'
              element={
                <ProtectedRoute>
                  <FAQPage />
                </ProtectedRoute>
              }
            />

            {/* fallback */}
            <Route path='*' element={<Navigate to={isAuthenticated ? '/' : '/login'} replace />} />
          </Routes>
          <Footer showDisclaimer={location.pathname === '/ad-review'} />
        </AntdApp>
      </ConfigProvider>
    </GoogleReCaptchaProvider>
  );
};

export default App;
